export interface ViewportSnapshot {
  width: number;
  height: number;
  pixelRatio: number;
  compact: boolean;
}

export class ViewportService {
  private readonly listeners = new Set<(viewport: ViewportSnapshot) => void>();
  private observer: ResizeObserver | null = null;
  private snapshot: ViewportSnapshot;

  public constructor(private readonly element: HTMLElement) {
    this.snapshot = this.measure();
  }

  public start(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.publish());
      this.observer.observe(this.element);
    } else {
      window.addEventListener('resize', this.publish);
    }
    window.visualViewport?.addEventListener('resize', this.publish);
    window.visualViewport?.addEventListener('scroll', this.publish);
    this.publish();
  }

  public subscribe(listener: (viewport: ViewportSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  public get value(): ViewportSnapshot {
    return this.snapshot;
  }

  public dispose(): void {
    this.observer?.disconnect();
    this.observer = null;
    window.removeEventListener('resize', this.publish);
    window.visualViewport?.removeEventListener('resize', this.publish);
    window.visualViewport?.removeEventListener('scroll', this.publish);
    this.listeners.clear();
  }

  private readonly publish = (): void => {
    this.snapshot = this.measure();
    document.documentElement.style.setProperty('--loom-vh', `${this.snapshot.height}px`);
    document.documentElement.style.setProperty('--loom-vw', `${this.snapshot.width}px`);
    for (const listener of this.listeners) listener(this.snapshot);
  };

  private measure(): ViewportSnapshot {
    const visual = window.visualViewport;
    const width = Math.max(320, Math.round(visual?.width ?? this.element.clientWidth ?? window.innerWidth));
    const height = Math.max(320, Math.round(visual?.height ?? this.element.clientHeight ?? window.innerHeight));
    return {
      width,
      height,
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      compact: width < 768,
    };
  }
}
